/**
 * Componente de Ruta Protegida por Rol
 * Path: backoffice-web/src/components/RoleRoute.jsx
 * Última modificación: 2026-01-03 19:30
 * Dev: cpalacios01@gmail.com | +595994648273
 * 
 * IMPORTANTE:
 * Este componente controla el acceso a las rutas según el rol del usuario.
 * Si el usuario no tiene permiso, lo redirige a su dashboard correspondiente.
 * 
 * ROLES:
 * - role_id = 1: Administrador (acceso total)
 * - role_id = 2: Conductor/Chofer (solo rutas /chofer/*)
 * - role_id = 3: Rendidor (rendiciones y redondos)
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const RoleRoute = ({ children, requiredPath }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" />;
  }

  // ========================================
  // DEFINICIÓN DE PERMISOS POR ROL
  // ========================================

  const permissions = {
    // ========================================
    // ADMINISTRADOR (role_id = 1)
    // Acceso TOTAL a todo el sistema
    // ========================================
    1: [
      '/',
      '/choferes',
      '/buses',
      '/usuarios',
      '/tarifas',
      '/cajas',
      '/reportes',
      '/perfil',
      '/itinerarios',
      '/redondos',
      '/gastos',
      // El admin también puede acceder a vistas de chofer si quiere
      '/chofer/dashboard',
      '/chofer/nueva-boleta',
      '/chofer/nuevo-gasto',
      '/chofer/detalle-redondo',
    ],

    // ========================================
    // CONDUCTOR/CHOFER (role_id = 2)
    // SOLO acceso a su módulo específico
    // ========================================
    2: [
      '/', // Dashboard principal (se redirige automáticamente a /chofer/dashboard)
      '/chofer/dashboard',
      '/chofer/nueva-boleta',
      '/chofer/nuevo-gasto',
      '/chofer/detalle-redondo',
      '/perfil', // Puede ver su perfil
      '/gastos', // Puede ver gastos (filtrado solo los suyos)
    ],

    // ========================================
    // RENDIDOR (role_id = 3)
    // Acceso a rendiciones y redondos
    // ========================================
    3: [
      '/',
      '/redondos',
      '/cajas',
      '/reportes',
      '/perfil',
      '/gastos', // Puede ver todos los gastos
      '/itinerarios', // Puede ver itinerarios
    ],
  };

  // Obtener permisos del usuario actual
  const userPermissions = permissions[user.role_id] || [];
  
  // Verificar si tiene permiso para esta ruta
  const hasPermission = userPermissions.includes(requiredPath);

  if (!hasPermission) {
    console.log(`❌ Usuario role_id=${user.role_id} sin permiso para: ${requiredPath}`);
    
    // Redirigir según el rol
    if (user.role_id === 2) {
      // Choferes van a su dashboard específico
      console.log('➡️ Redirigiendo chofer a /chofer/dashboard');
      return <Navigate to="/chofer/dashboard" replace />;
    }
    
    // Otros roles van al dashboard general
    console.log('➡️ Redirigiendo a dashboard general /');
    return <Navigate to="/" replace />;
  }

  console.log(`✅ Usuario role_id=${user.role_id} tiene permiso para: ${requiredPath}`);
  return children;
};

export default RoleRoute;

// cpalacios01@gmail.com | +595994648273
